import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaLinkedin, FaBars, FaTimes } from 'react-icons/fa';
import { SiLeetcode, SiHackerrank } from 'react-icons/si';

const Nav = styled.nav`
  padding: 1.5rem 1rem;
  position: fixed;
  left: 0;
  top: 0;
  width: 280px;
  height: 100vh;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1000;
  transition: transform 0.3s ease;

  @media (max-width: 768px) {
    width: 100%;
    transform: translateX(${props => props.isOpen ? '0' : '-100%'});
  }
`;

const MenuButton = styled.button`
  display: none;
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 1001;
  background: none;
  border: none;
  color: #333;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Overlay = styled(motion.div)`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;

  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
  }
`;

const ProfileImage = styled.img`
  width: 180px;
  height: 180px;
  border-radius: 50%;
  margin-bottom: 1rem;
  display: block;
  object-fit: cover;
  object-position: center 20%;
  box-shadow:
    0 0 0 4px rgba(0,123,255,0.10), /* softer blue glow, less radius */
    0 4px 16px rgba(0,123,255,0.10); /* softer blue shadow */
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-font-smoothing: subpixel-antialiased;
`;

const Name = styled.h1`
  font-size: 1.6rem;
  margin: 0.5rem 0;
  font-family: 'Playfair Display', serif;
  font-weight: 400;
  color: #000;
  line-height: 1.2;
  text-align: center;
  width: 100%;
`;

const Title = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 0.65rem;
  font-weight: 300;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #007bff;
  margin-bottom: 1.5rem;
  text-align: center;
  white-space: nowrap;
  width: 100%;
`;

const NavLinks = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 1rem;
  padding-left: 1rem;
`;

const NavLink = styled(motion.a)`
  text-decoration: none;
  color: #666666;
  padding: 0.6rem 1rem;
  margin: 0.2rem 0;
  font-size: 0.85rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  cursor: pointer;
  width: calc(100% - 1rem);

  &:hover {
    color: #007bff;
    background: rgba(0, 123, 255, 0.05);
    padding-left: 1.5rem;
  }

  &.active {
    font-weight: 500;
    color: #007bff;
    padding-left: 1.5rem;
    background: rgba(0, 123, 255, 0.05);
  }
`;

const Footer = styled.footer`
  width: 100%;
  padding: 0.5rem 1rem;
  padding-left: 1.5rem;
  margin-top: 0.5rem;
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 1.2rem;
  justify-content: flex-start;
  padding-left: 0.5rem;
`;

const SocialIcon = styled.a`
  color: ${props => {
    switch(props.platform) {
      case 'leetcode':
        return '#FFA116';
      case 'hackerrank':
        return '#2EC866';
      case 'github':
        return '#333333';
      case 'linkedin':
        return '#0A66C2';
      default:
        return '#666666';
    }
  }};
  font-size: 1.3rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    filter: brightness(1.2);
  }
`;

const Navbar = () => {
  const [activeLink, setActiveLink] = useState('home');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'services', 'skills', 'education', 'projects', 'contact'];
      const scrollPosition = window.scrollY;

      sections.forEach(section => {
        const element = document.getElementById(section);
        if (element) {
          const top = element.offsetTop - 100;
          const height = element.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveLink(section);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = (id) => {
    setActiveLink(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    // Close menu on mobile after clicking a link
    if (window.innerWidth <= 768) {
      setIsOpen(false);
    }
  };

  return (
    <>
      <MenuButton onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <FaTimes /> : <FaBars />}
      </MenuButton>
      <AnimatePresence>
        {isOpen && <Overlay isOpen={isOpen} onClick={() => setIsOpen(false)} />}
      </AnimatePresence>
      <Nav isOpen={isOpen}>
      <ProfileImage src="/images/portfolioimages1.jpg" alt="Profile" />
      <Name>SHLOK SONKER</Name>
      <Title>Frontend Developer / Data Analyst</Title>
      
      <NavLinks>
        {['HOME', 'ABOUT', 'SERVICES', 'SKILLS', 'EDUCATION', 'PROJECTS', 'CONTACT'].map((item) => (
          <NavLink 
            key={item}
            onClick={() => handleClick(item.toLowerCase())}
            className={activeLink === item.toLowerCase() ? 'active' : ''}
            whileHover={{ x: 10 }}
          >
            {item}
          </NavLink>
        ))}
      </NavLinks>

      <Footer>
        <SocialIcons>
          <SocialIcon href="https://leetcode.com/u/shloksonkar/" target="_blank" rel="noopener noreferrer" platform="leetcode">
            <SiLeetcode />
          </SocialIcon>
          <SocialIcon href="https://www.hackerrank.com/profile/sonkarshloke" target="_blank" rel="noopener noreferrer" platform="hackerrank">
            <SiHackerrank />
          </SocialIcon>
          <SocialIcon href="https://github.com/Shlok2k/About-me-" target="_blank" rel="noopener noreferrer" platform="github">
            <FaGithub />
          </SocialIcon>
          <SocialIcon href="https://www.linkedin.com/in/shlok-sonker/" target="_blank" rel="noopener noreferrer" platform="linkedin">
            <FaLinkedin />
          </SocialIcon>
        </SocialIcons>
      </Footer>
    </Nav>
    </>
  );
};

export default Navbar; 